<template>
  <FormLayout
    :has-changes="hasChanges"
    :is-loading="!data.name"
    :is-saving="data.isSaving"
    :title="data.name"
    overline="EDITING LOCATION"
    @on-dismiss="handleDismiss"
    @on-save="handleSave"
  >
    <vs-form>
      <vs-autocomplete
        v-if="statusesForAutocomplete"
        :items="statusesForAutocomplete"
        :value="data.statusId"
        data-test="autocomplete"
        label="Status"
        placeholder="Select a status"
        @input="handleUpdateStatus"
      >
        <template #outer-append>
          <v-avatar v-if="data.statusId" :color="statusColor" class="ml-2" size="48" />
        </template>
      </vs-autocomplete>

      <vs-text-input
        :value="data.description"
        label="Description"
        multiline
        @input="handleUpdateDescription"
      />

      <schema-field
        v-if="schema && schema.properties"
        :error-schema="data.errorSchema"
        :form-data="data.customData"
        :form-resolver="contextualizedFormResolver"
        :params="{ headingType: 'heading' }"
        :root-schema="schema"
        :schema="schema"
        :ui-schema="uiSchema"
        @change="handleUpdateCustomData"
      />
    </vs-form>
  </FormLayout>
</template>

<script async lang="ts" setup>
import { computed, onMounted, reactive } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import FormLayout from '../common/FormLayout.vue'
import { ContextualizedFormResolver } from '@/utils/forms/contextualized-form-resolver'
import { FormContext } from '@/utils/forms/form-context'
import { cloneDeep, isEmpty, isEqual } from 'lodash'
import { resolveFormDataForValidation } from '@/utils/forms/form-resolver'
import { validateFormData } from '@/utils/forms/form-validate'
import CurrentUserContext from '@/models/current-user-context'
import { updateLocationDetails } from '@/api/account-data/manage-locations'
import { useGetLocationStatusesQuery } from '@/api/locations'

const { data: statuses } = useGetLocationStatusesQuery()
const statusesForAutocomplete = computed(() => {
  return statuses.value?.map((s) => ({
    text: s.name,
    value: s.id,
    color: s.hexCode,
  }))
})

const store = useStore()

const getCurrentUserContext = computed<CurrentUserContext>(
  () => store.getters['permissions/getContext'],
)
interface LocationTypeCustomFieldsSchema {
  schema?: {
    properties: object
  }
  uiSchema?: object
}
interface Props {
  statusId?: number | null
  description?: string | null
  name: string
  locationId: number | string
  rootSchema: LocationTypeCustomFieldsSchema | null
  customData: object | null
}

const props = withDefaults(defineProps<Props>(), {
  statusId: null,
  description: null,
  name: '',
  locationId: '',
  rootSchema: null,
  customData: null,
})

interface Data {
  isValid: boolean
  statusId: number | null
  description: string | null
  name: string | null
  isSaving: boolean
  errorSchema: object
  customData: object
}

const data: Data = reactive({
  isValid: true,
  statusId: null,
  description: null,
  name: '',
  isSaving: false,
  errorSchema: {},
  customData: {},
})

const statusColor = computed(() => {
  return statuses.value?.find((s) => s.id === data.statusId)?.hexCode
})

const hasChanges = computed(() => {
  return (
    data.statusId !== props.statusId ||
    data.description !== props.description ||
    !isEqual(data.customData, props.customData)
  )
})

const schema = computed(() => {
  return props.rootSchema?.schema
})

const uiSchema = computed(() => {
  return props.rootSchema?.uiSchema
})

const handleUpdateStatus = (value) => {
  data.statusId = value
}

const handleUpdateDescription = (value) => {
  data.description = value
}

const handleUpdateCustomData = (value) => {
  if (customFieldsAreValid()) {
    data.customData = value
  }
}

const customFieldsAreValid = () => {
  const resolvedSchema = contextualizedFormResolver.value.retrieveSchema(
    schema,
    schema,
    data.customData,
  )
  const resolvedData = resolveFormDataForValidation(data.customData)

  const errorSchema = validateFormData(resolvedSchema.value, resolvedData)
  data.errorSchema = errorSchema

  return isEmpty(errorSchema)
}

const contextualizedFormResolver = computed(() => {
  const currentUserContext = getCurrentUserContext.value
  const currentUser = {
    id: currentUserContext?.userSid,
    name: currentUserContext?.name,
  }
  const context = new FormContext({
    hub: null,
    currentUser,
    additionalContext: '',
  })
  return new ContextualizedFormResolver(context)
})

onMounted(() => {
  data.statusId = props.statusId
  data.description = props.description
  data.name = props.name
  data.customData = cloneDeep(props.customData) ?? {}
})

const emits = defineEmits(['dismiss', 'save'])

const handleDismiss = () => {
  emits('dismiss')
}
const handleSave = async () => {
  data.isSaving = true
  await updateLocationDetails(props.locationId, data.statusId, data.description, data.customData)
  data.isSaving = false
  emits('save')
}
</script>
